import { BOGGLE_MORE_COMMON_LETTER_ALLOWED_VALUES } from '~/boggle/config/challengeModLetterConstants'
import type { BoggleChallengeBoardDistributionDefinition } from '~/boggle/config/boardDistributions/boardDistributionDefinition'
import type { BoggleResolvedChallengeBoardDistribution } from '~/boggle/config/challenges/modTypes'
import { pickWeightedEntry } from '~/boggle/lib/challenge/random'
import {
  BOGGLE_MORE_COMMON_MULTIPLIER_VALUES,
  MORE_COMMON_LETTER_PARAMETER_VARIANTS,
  getBoggleMoreCommonLetterMultiplier,
} from '~/boggle/lib/challenge/parameterVariants'
import { formatResolvedBoardDistributionDisplayFromDefinition } from '~/boggle/config/boardDistributions/formatResolvedBoardDistributionDisplay'

export const moreCommonLetterBoardDistributionDefinition: BoggleChallengeBoardDistributionDefinition =
  {
    description: 'Some letters appear more often',
    formatResolvedDisplay: ({ parameters }) => {
      const letter = String(parameters.letter?.value ?? '')
      const multiplier = Number(parameters.multiplier?.value ?? 1)
      if (letter.length === 0) {
        return {
          description: 'Some letters appear more often',
          name: 'X is more common',
          shortLabel: 'Letter boost',
        }
      }

      return {
        description: `${letter} is very common`,
        name: 'Letter Boost',
        shortLabel: `${letter} x${multiplier}`,
      }
    },
    id: 'more-common-letter',
    name: 'X is more common',
    parameterDefinitions: {
      letter: {
        kind: 'enum',
        max: BOGGLE_MORE_COMMON_LETTER_ALLOWED_VALUES.length - 1,
        min: 0,
        values: BOGGLE_MORE_COMMON_LETTER_ALLOWED_VALUES,
      },
      multiplier: {
        kind: 'enum',
        max: BOGGLE_MORE_COMMON_MULTIPLIER_VALUES.length - 1,
        min: 0,
        values: BOGGLE_MORE_COMMON_MULTIPLIER_VALUES,
      },
    },
    selectionWeight: 5,
    shortLabel: 'Letter boost',
  }

export function resolveMoreCommonLetterBoardDistribution(
  family: BoggleChallengeBoardDistributionDefinition,
  rngState: number,
): {
  mod: BoggleResolvedChallengeBoardDistribution
  rngState: number
} {
  const pickedLetter = pickWeightedEntry(MORE_COMMON_LETTER_PARAMETER_VARIANTS, rngState)
  if (pickedLetter === null) {
    throw new Error(`No boosted letters available for ${family.id}`)
  }

  const multiplier = getBoggleMoreCommonLetterMultiplier(pickedLetter.entry.letter)
  const parameters = {
    letter: {
      definition: family.parameterDefinitions.letter!,
      value: pickedLetter.entry.letter,
    },
    multiplier: {
      definition: family.parameterDefinitions.multiplier!,
      value: multiplier,
    },
  }
  const display = formatResolvedBoardDistributionDisplayFromDefinition(family, parameters)

  return {
    mod: {
      description: display.description,
      effects: [
        {
          kind: 'letter-weights',
          transform: {
            kind: 'multiply-letter-weight',
            letter: pickedLetter.entry.letter,
            multiplier,
          },
        },
      ],
      familyId: family.id,
      id: `${pickedLetter.entry.letter.toLowerCase()}-x${multiplier}`,
      name: display.name,
      outcomes: [
        {
          kind: 'boosted-letter',
          letter: pickedLetter.entry.letter,
          multiplier,
        },
      ],
      parameters,
      shortLabel: display.shortLabel,
    },
    rngState: pickedLetter.rngState,
  }
}
